import { useState } from 'react';

import type { Booking, ExtendBookingInput } from '@/api/bookings';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Button, Card, Notice, PageHeading } from '@/components/PageHeading';
import { Field, controlClass } from '@/components/Field';
import { StatusBadge } from '@/components/StatusBadge';
import { formatAmount } from '@core/format/money';
import { formatDate, toIsoDate } from '@core/format/dates';
import {
  extensionProblem,
  extraDays,
  garmentsStillOut,
  maxExtensionDays,
  quoteExtension,
} from '@core/bookings/extension';
import { withReference } from '@core/units/referenceNumber';

/**
 * Granting more days on a booking already out (spec Section 8, item 2.6).
 *
 * <p>
 * <strong>A page, not a drawer</strong>, per the rule settled 2026-09-08: a screen that writes a
 * record is a page. Reached by internal state from the booking detail, because the shell navigates
 * by key and has no router.
 * </p>
 *
 * <p>
 * <strong>The figure is quoted before Save, and that is the point of the screen.</strong> "Two more
 * days, sixteen hundred" is the sentence a clerk says to a customer standing at the counter, and it
 * has to be true before either of them agrees to it. What is printed is this browser's arithmetic
 * over the rates the card froze; the record keeps the server's.
 * </p>
 *
 * <p>
 * <strong>It says out loud what the extension does to a late fee</strong>, because that is the part
 * a shop is most likely to get wrong at the counter: the days it covers stop counting as overdue, so
 * a day is charged once rather than twice.
 * </p>
 */
export function ExtendBookingPage({
  booking,
  busy = false,
  onCancel,
  onSave,
}: {
  booking: Booking;
  busy?: boolean;
  onCancel: () => void;
  onSave: (input: ExtendBookingInput) => void;
}) {
  const firstField = useAutoFocusFirstField();

  const stillOut = garmentsStillOut(booking);

  // The shop's own rate, for a garment whose card priced no extra day. Read off the booking rather
  // than taken as a prop — a prop nothing passes is the recurring defect wearing a default value.
  const fallbackPerDay = booking.lateFeeTerms.shopPerDay;

  const [toDate, setToDate] = useState(() => {
    // Opened on the day after the booking currently ends, which is the shortest real answer and the
    // one a clerk most often wants — never on today, which for an overdue booking would silently
    // propose an extension that ends before the garments are even back.
    const next = new Date(`${booking.possessionToDate.slice(0, 10)}T00:00:00Z`);

    next.setUTCDate(next.getUTCDate() + 1);

    return toIsoDate(next) ?? '';
  });

  const [notes, setNotes] = useState('');

  const days = extraDays(booking.possessionToDate, toDate);
  const quote = quoteExtension(stillOut, days, fallbackPerDay);

  const problem = extensionProblem({
    booking,
    toDate,
    days,
    unpriced: quote.unpriced,
    maxDays: maxExtensionDays,
  });

  return (
    <div className="flex flex-col gap-3 lg:gap-4" {...enterKeyScope}>
      <PageHeading
        title="Keep it longer"
        subtitle={`${booking.documentNumber || 'This booking'} · back on ${formatDate(booking.possessionToDate)}`}
        actions={
          <>
            <Button onClick={onCancel} disabled={busy} shortcut="back">
              Cancel
            </Button>
            <Button
              variant="primary"
              icon="icon icon-check"
              data-enter-key-commit
              shortcut="save"
              loading={busy}
              disabled={problem !== null || busy}
              onClick={() => onSave({ toDate, notes: notes.trim() || null })}
            >
              Grant the extension
            </Button>
          </>
        }
      />

      {booking.isOverdue && (
        <Notice tone="warning" title="This booking is already late">
          {booking.daysOverdue === 1 ? 'A day' : `${booking.daysOverdue} days`} past its return
          date. An extension covering those days stops them counting as late — they are charged
          once, at the same rate, as days the shop agreed to.
        </Notice>
      )}

      <div className="grid gap-3 lg:grid-cols-2 lg:gap-4">
        <Card>
          <p className="mb-2.5 text-sm font-semibold text-gray-900">Until when</p>

          <div className="flex flex-col gap-3">
            <Field label="Back on" required hint="The last day the customer keeps them, inclusive.">
              <input
                ref={firstField}
                type="date"
                disabled={busy}
                value={toDate}
                onChange={(event) => setToDate(event.target.value)}
                className={controlClass}
              />
            </Field>

            <Field label="Notes">
              <textarea
                rows={2}
                disabled={busy}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                className={controlClass}
              />
            </Field>
          </div>

          {/*
            Named rather than counted. A clerk granting an extension is looking at a customer
            holding specific garments, and "3 garments" is not something either of them can check.
          */}
          <div className="mt-3">
            <p className="text-default mb-1.5 text-[length:var(--scale-list-meta)]">
              Still out, and charged for the extra days
            </p>

            <ul className="flex list-none flex-col gap-1 ps-0">
              {stillOut.map((garment) => (
                <li
                  key={garment.id}
                  className="flex list-none items-baseline justify-between gap-2"
                >
                  <span className="text-[length:var(--scale-list-name)] text-gray-900">
                    {withReference(garment.serialCode, garment.referenceNumber)}
                    <span className="text-default ms-1.5">{garment.itemName}</span>
                  </span>

                  <span className="text-default text-[length:var(--scale-list-meta)] whitespace-nowrap">
                    {garment.extensionRatePerDay === null && fallbackPerDay === null ? (
                      <StatusBadge tone="warning">No day rate</StatusBadge>
                    ) : (
                      <>{formatAmount(garment.extensionRatePerDay ?? fallbackPerDay ?? 0)} / day</>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </Card>

        <Card>
          <p className="mb-2.5 text-sm font-semibold text-gray-900">What it comes to</p>

          <dl className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between gap-2">
              <dt className="text-default text-[length:var(--scale-list-meta)]">Extra days</dt>
              <dd className="text-[length:var(--scale-list-name)] font-semibold text-gray-900">
                {days > 0 ? days : '—'}
              </dd>
            </div>

            <div className="border-border-color flex items-baseline justify-between gap-2 border-t pt-2">
              <dt className="text-[length:var(--scale-list-name)] font-semibold text-gray-900">
                To pay for the extension
              </dt>
              <dd className="text-lg font-bold text-gray-900">{formatAmount(quote.total)}</dd>
            </div>
          </dl>

          {/*
            Said here rather than left to be discovered: the extension records what is owed and does
            not take the money, so a clerk who does not collect it has a booking that reads as paid
            up and is not.
          */}
          <Notice tone="info" title="This does not take the money">
            The charge goes onto the booking and a debit note is raised for it. Collect it at the
            counter the way you would any other payment.
          </Notice>

          {problem && (
            <p className="text-warning mt-3 text-[length:var(--scale-list-meta)]">{problem}</p>
          )}
        </Card>
      </div>
    </div>
  );
}
